import dayjs from "dayjs";
import has from "lodash/has";
import { t } from 'i18next';
import states from "../../public/data/states.json";
import { fetchTripByTripNumber, getAllTrips } from "../../api/tripService";
import { getAllShipments } from "../../api/shipmentService";
import { getAllCarriers } from "../../api/catalogService";
import { getOrganizations, getAllUsers, getAllTenants, getOrganizationUsers, getPortalNotification } from "../../api/users"
import conveyanceTypes from "../../public/data/conveyanceTypes.json";
import equipmentTypes from "../../public/data/equipmentTypes.json";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import moment from 'moment'
dayjs.extend(utc);
dayjs.extend(timezone);
const localTimezone = dayjs.tz.guess();

export const formatConveyanceOptions = (conveyances, carrier) => {
  if (!conveyances || !Array.isArray(conveyances.data)) {
    return [];
  }
  return conveyances.data
    .filter(({ carrierId }) => carrierId === carrier)
    .map(({ conveyanceNumber, primaryPlate, conveyanceId }) => ({
      text: `${conveyanceNumber} - ${primaryPlate}`,
      value: conveyanceId
    }));
};

export const formatEquipmentOptions = (equipments, carrier) => {
  if (!equipments || !Array.isArray(equipments.data)) {
    return []; // Retornar un array vacío si equipments no es válido
  }
  return equipments.data
    .filter(({ carrierId }) => carrierId === carrier)
    .map(({ equipmentNumber, primaryPlate, equipmentId }) => ({
      text: `${equipmentNumber} - ${primaryPlate}`,
      value: equipmentId
    }));
};

export const formatDriverOptions = (drivers, carrier) => {
  if (!drivers || !Array.isArray(drivers.data)) {
    return [];
  }
  return drivers.data
    .filter(({ carrierId }) => carrierId === carrier)
    .map(({ firstName, lastName, driverId }) => ({
      text: `${firstName} ${lastName}`,
      value: driverId
    }));
};

export const formatPassengerOptions = (passengers, carrier) => {
  if (!passengers || !Array.isArray(passengers.data)) {
    return [];
  }
  return passengers.data
    .filter(({ carrierId }) => carrierId === carrier)
    .map(({ firstName, lastName, passengerId }) => ({
      text: `${firstName} ${lastName}`,
      value: passengerId
    }));
};

export const formatPortOptions = (ports) => {
  return ports.data.map(({ portCode, portName, portId }) => ({
    text: `${portName} - ${portCode}`,
    value: portId
  }));
};

export const formatCarrierOptions = (carriers) => {
  return carriers.data.map(({ carrierName, carrierId, scac }) => ({
    text: `${carrierName} - ${scac}`,
    aliasText: scac,
    value: carrierId
  }));
};

export const formatTripEditOptions = (catalogs, carrierId) => {
  const options = {};

  if (catalogs?.drivers?.data?.length) {
    options.driverOptions = formatDriverOptions(catalogs.drivers, carrierId);
  }
  if (catalogs?.equipments?.data?.length) {
    options.equipmentOptions = formatEquipmentOptions(catalogs.equipments, carrierId);
  }
  if (catalogs?.conveyances?.data?.length) {
    options.conveyanceOptions = formatConveyanceOptions(catalogs.conveyances, carrierId);
  }
  if (catalogs?.passengers?.data?.length) {
    options.passengerOptions = formatPassengerOptions(catalogs.passengers, carrierId);
  }

  return options;
};

export const mapTripValues = (tripDetails) => {
  const trip = {
    tripNumber: "",
    arrivalDate: "",
    arrivalDetails: "",
    driver: "",
    driverFast: "",
    driverLicense: "",
    passenger: "",
    secondaryDriver: ""
  };
  if (tripDetails) {
    trip.tripNumber = `${tripDetails.scac}-${tripDetails.tripNumber}`;
    trip.arrivalDate = dayjs(tripDetails.arrival).format("MM/DD/YY hh:mm A");

    if (
      has(tripDetails, "drivers") &&
      tripDetails.drivers &&
      tripDetails.drivers.length
    ) {
      const [primaryDriver, secondaryDriver] = tripDetails.drivers;
      trip.driver = `${primaryDriver.firstName} ${primaryDriver.lastName}`;
      trip.driverFast = primaryDriver.fastCardNumber
        ? `FAST - ${primaryDriver.fastCardNumber}`
        : null;
      trip.driverLicense = primaryDriver.licenseNumber
        ? `License - ${primaryDriver.licenseNumber}`
        : null;

      if (secondaryDriver) {
        trip.secondaryDriver = `${secondaryDriver.firstName} ${secondaryDriver.lastName}`;
      }
    }

    const {
      arrivalPortName,
      arrivalPortCode,
      arrivalPortStateCode,
      arrivalPortCountryCode
    } = tripDetails;

    const state = states.find((s) => s.code === arrivalPortStateCode);

    trip.arrivalDetails = `${arrivalPortName}(${arrivalPortCode}), ${state?.name || arrivalPortStateCode || "N/A"
      }, ${arrivalPortCountryCode || "N/A"}`;

    let newDate1 = new Date(tripDetails.arrived);
    let offset1 = newDate1.getTimezoneOffset() / 60;
    let hours1 = newDate1.getHours();
    newDate1.setHours(hours1 - offset1);

    let newDate2 = new Date(tripDetails.released);
    let offset2 = newDate2.getTimezoneOffset() / 60;
    let hours2 = newDate2.getHours();
    newDate2.setHours(hours2 - offset2);

    // trip.arrived = tripDetails.arrived ? dayjs.tz(new Date(tripDetails.arrived), localTimezone).format('DD/MM/YY hh:mm A') : ''
    // trip.released = tripDetails.released ? dayjs.tz(new Date(tripDetails.released), localTimezone).format('DD/MM/YY hh:mm A') : ''
    trip.arrived = tripDetails.arrived ? moment(newDate1).format('DD/MM/YYYY hh:mm A') : ''
    trip.released = tripDetails.released ? moment(newDate2).format('DD/MM/YYYY hh:mm A') : ''
  }
  return trip;
};

export const getEquipmentCardInfo = (equipment) => {
  const type = equipmentTypes.find((s) => s.id == equipment.equipmentTypeId);
  return {
    title: `${equipment.equipmentNumber} - ${equipment.primaryPlate}`,
    details: `${type?.name}, (${equipment.primaryPlateStateCode}, ${equipment.primaryPlateCountryCode})`
  };
};

export const getDriverCardInfo = (driver) => {
  const state = states.find((s) => s.code === driver.licenseStateCode);
  return {
    title: `${driver.firstName} ${driver.lastName}`,
    details: `License: ${driver.licenseNumber}, (${state?.name}, ${driver.licenseCountryCode})`,
    fast: driver?.fastCardNumber ? driver?.fastCardNumber : null
  };
};

export const getPassengerCardInfo = (passenger) => {
  let details = `Passport: ${passenger.passportNumber}, ${passenger.nationalityCountryCode})`;
  if (passenger.passportCountry) {
    details = `Passport: ${passenger.passportNumber}, (${passenger.passportCountry})`;
  } else if (passenger.birthCertificateNumber) {
    details = `Birth Certificate: ${passenger.birthCertificateNumber}, ${passenger.nationalityCountryCode})`;
  } else if (passenger.permanentResidentCardNumber) {
    details = `Permanent Resident Card: ${passenger.permanentResidentCardNumber}, ${passenger.nationalityCountryCode})`;
  } else if (passenger.certificateOfNaturalizationNumber) {
    details = `Certificate of Naturalization: ${passenger.certificateOfNaturalizationNumber}, ${passenger.nationalityCountryCode})`;
  } else if (passenger.citizenshipCardNumber) {
    details = `Citizenship Card: ${passenger.citizenshipCardNumber}, ${passenger.nationalityCountryCode})`;
  } else if (passenger.reentryPermitNumber) {
    details = `Reentry Permit: ${passenger.reentryPermitNumber}, ${passenger.nationalityCountryCode})`;
  }

  return {
    title: `${passenger.firstName} ${passenger.lastName}`,
    details: details,
    fast: passenger?.fastCardNumber ? passenger?.fastCardNumber : null
  };
};

export const getConveyanceCardInfo = ({
  conveyanceNumber,
  conveyanceTypeCode,
  conveyanceStateCode,
  conveyanceCountryCode,
  conveyancePrimaryPlate
}) => {
  const state = states.find((s) => s.code === conveyanceStateCode);
  let type = conveyanceTypes.find((s) => s.code == conveyanceTypeCode);

  if (!type) {
    type = conveyanceTypes.find((c) => c.id === Number(conveyanceTypeCode));
  }

  return {
    title: `${conveyanceNumber} - ${conveyancePrimaryPlate}`,
    details: `${(type && type.name) || ""}, (${state?.name || conveyanceStateCode
      }, ${conveyanceCountryCode})`,
    conveyance: {
      conveyanceNumber,
      conveyanceTypeCode,
      conveyanceStateCode,
      conveyanceCountryCode,
      conveyancePrimaryPlate
    }
  };
};

export const getEntityInfo = (entity, infoKeys) => {
  return Object.keys(infoKeys).map((key, index) => {
    const entityInfo = infoKeys[key];

    return {
      title: index === 0 ? "Info" : "Secondary Info",
      body: Object.keys(entityInfo).map((equipmentKey) => {
        const info = entityInfo[equipmentKey];
        return {
          label: info.label,
          value: entity[info.key] || "N/A"
        };
      })
    };
  });
};

export const getTripFetchBySpecification = (directionTrip = "CBP") => {
  return {
    fetchBy: {
      carriers: {
        type: "carriers",
        dataKey: "data",
        mapStrategy: (carrier) => {
          const { carrierName, carrierId, scac } = carrier;
          return {
            text: carrierName ? `${carrierName} - ${scac}` : scac,
            aliasText: scac,
            value: carrierId,
            payload: carrier
          };
        }
      },
      ports: (country) => ({
        type: "ports",
        dataKey: "data",
        customSearchKey: "portCountryCode",
        customSearchValue: country,
        mapStrategy: (port) => {
          const { portCode, portId, portName, portCountryCode } = port;
          if (directionTrip == "CBP" && portCountryCode === "US") {
            return {
              text: `${portName} - ${portCode} - ${portCountryCode}`,
              value: portId,
              payload: port
            };
          } else if (directionTrip == "CBSA" && portCountryCode === "CA") {

            return {
              text: `${portName} - ${portCode} - ${portCountryCode}`,
              value: portId,
              payload: port
            };
          } else {
            return null;
          }
        }
      }),
      // ports: {
      //   type: "ports",
      //   dataKey: "data",
      //   mapStrategy: (port) => {
      //     const { portCode, portName, portId } = port;
      //     return {
      //       text: `${portName} - ${portCode}`,
      //       value: portId,
      //       payload: port
      //     };
      //   }
      // },
      conveyances: (carrier) => ({
        type: "conveyances",
        dataKey: "data",
        customSearchKey: "carrierId",
        customSearchValue: carrier,
        mapStrategy: (conveyance) => {
          const { conveyanceNumber, primaryPlate, conveyanceId, primaryPlateStateCode, primaryPlateCountryCode } = conveyance;
          return {
            text: `${primaryPlate} (${conveyanceNumber}, ${primaryPlateStateCode}, ${primaryPlateCountryCode})`,
            value: conveyanceId,
            payload: conveyance
          };
        }
      }),
      equipments: (carrier) => ({
        type: "equipments",
        dataKey: "data",
        customSearchKey: "carrierId",
        customSearchValue: carrier,
        mapStrategy: (equipment) => {
          const { equipmentNumber, primaryPlate, equipmentId, primaryPlateStateCode, primaryPlateCountryCode } = equipment;
          return {
            text: `${primaryPlate} (${equipmentNumber}, ${primaryPlateStateCode}, ${primaryPlateCountryCode})`,
            value: equipmentId,
            payload: equipment
          };
        }
      }),
      drivers: (carrier) => ({
        type: "drivers",
        dataKey: "data",
        customSearchKey: "carrierId",
        customSearchValue: carrier,
        mapStrategy: (driver) => {
          const { firstName, lastName, driverId } = driver;
          return {
            text: `${firstName} ${lastName}`,
            value: driverId,
            payload: driver
          };
        }
      }),
      passengers: (carrier) => ({
        type: "passengers",
        dataKey: "data",
        customSearchKey: "carrierId",
        customSearchValue: carrier,
        mapStrategy: (passenger) => {
          const { firstName, lastName, passengerId } = passenger;
          return {
            text: `${firstName} ${lastName}`,
            value: passengerId,
            payload: passenger
          };
        }
      }),
      consignees: {
        type: "parties",
        customSearchKey: "country",
        customSearchValue: directionTrip === "CBSA" ? "CA" : "US",
        dataKey: "data",
        mapStrategy: (consignee) => {
          if (!consignee) return null;

          const { country, name, _id, id } = consignee;

          // Determinar el nombre a mostrar
          let displayName = name;

          // Si no hay nombre pero hay dirección, construir el nombre
          if (!displayName && (consignee.address1 || consignee.city)) {
            const parts = [
              consignee.address1,
              consignee.city,
              consignee.stateProvince,
              consignee.postal
            ].filter(Boolean);

            displayName = parts.length > 0
              ? parts.join(', ')
              : `${country || 'US'} Address`;
          }

          return {
            text: `${country || 'US'} ${displayName || 'Address'}`,
            value: _id || id,
            payload: consignee
          };
        }
      }
      // consignees: {
      //   type: "parties",
      //   customSearchKey: "country",
      //   customSearchValue: directionTrip === "CBSA" ? "CA" : "US",
      //   dataKey: "data",
      //   mapStrategy: (consignee) => {
      //     if (consignee) {
      //       const { country, name, _id, id } = consignee;
      //       return {
      //         text: `${country} ${name}`,
      //         value: _id || id,
      //         payload: consignee
      //       };
      //     }
      //   }
      // }
    }
  };
};

export const getTripsFetchBySpecification = () => ({
  fetchBy: {
    trips: {
      type: "trips",
      dataKey: "data",
      mapStrategy: (trip) => {
        const { id, scac, tripNumber } = trip;
        return {
          text: `${scac}-${tripNumber}`,
          value: id,
          payload: trip
        };
      },
      customFetchFunction: fetchTripByTripNumber
    },
    allTrips: {
      type: "trips",
      dataKey: "data",
      mapStrategy: (trip) => {
        const { id, scac, tripNumber } = trip;
        return {
          text: `${scac}-${tripNumber}`,
          value: id,
          payload: trip
        };
      },
      customFetchFunction: getAllTrips
    }
  }
});

export const getShipmentsFetchBySpecification = () => ({
  fetchBy: {
    allShipments: {
      type: 'allShipments',
      dataKey: 'shipments',
      mapStrategy: (shipment) => {
        const { shipmentId, scn, scac } = shipment;
        return {
          text: `${scac}-${scn}`,
          value: shipmentId,
          payload: shipment,
        };

      },
      customFetchFunction: getAllShipments
    }
  }
})

export const getOrganizationsFetchBySpecification = () => ({
  fetchBy: {
    organizations: {
      type: 'organizations',
      dataKey: null,
      mapStrategy: (organization) => {
        const { id, name } = organization;
        return {
          text: `${name}`,
          value: id,
          payload: organization,
        };
      },
      customFetchFunction: getOrganizations,
    },
  },
});

export const getUsersFetchBySpecification = () => ({
  fetchBy: {
    allUsers: {
      type: 'allUsers',
      dataKey: 'allUsers',
      mapStrategy: (user) => {
        const { id, firstName, lastName, email } = user;
        return {
          text: `${firstName} ${lastName} - ${email}`,
          value: id,
          payload: user,
        };
      },
      customFetchFunction: getAllUsers,
    },
  },
});

export const getOrganizationsColumnDefHideActions = (onEdit) => {
  const formName = "organizations"
  return [
    {
      field: 'name',
      headerName: `${t('Name')}`,
      cellClass: 'hide-selection-checkbox'
    },
    {
      field: 'tenantName',
      headerName: `${t('Tenant')}`,
      cellClass: 'hide-selection-checkbox',
      editable: false,
    },
    {
      field: 'phoneNumber',
      headerName: `${t('Phone')}`,
      cellClass: 'hide-selection-checkbox',
      editable: false,
      valueGetter: ({ data }) => {
        if (data.phoneNumber && data.phoneNumber.toLowerCase().includes('000')) {
          return ''; // Retorna una cadena vacía si el campo contiene 'empty'
        } else {
          return data.phoneNumber; // De lo contrario, devuelve el valor original del campo
        }
      }
    },
    {
      field: 'streetAddress',
      headerName: `${t('Street Address')}`,
      cellClass: 'hide-selection-checkbox',
      editable: false,
      valueGetter: ({ data }) => {
        if (data.streetAddress && data.streetAddress.toLowerCase().includes('000')) {
          return ''; // Retorna una cadena vacía si el campo contiene 'empty'
        } else {
          return data.streetAddress; // De lo contrario, devuelve el valor original del campo
        }
      }
    },
    {
      field: 'city',
      headerName: `${t('City')}`,
      cellClass: 'hide-selection-checkbox',
      editable: false,
      valueGetter: ({ data }) => {
        if (data.city && data.city.toLowerCase().includes('empty')) {
          return ''; // Retorna una cadena vacía si el campo contiene 'empty'
        } else {
          return data.city; // De lo contrario, devuelve el valor original del campo
        }
      }
    },
    {
      field: 'state',
      headerName: `${t('State')}`,
      cellClass: 'hide-selection-checkbox',
      editable: false,
      valueGetter: ({ data }) => {
        if (data.state && data.state.toLowerCase().includes('empty')) {
          return ''; // Retorna una cadena vacía si el campo contiene 'empty'
        } else {
          return data.state; // De lo contrario, devuelve el valor original del campo
        }
      }
    },
    {
      field: 'country',
      headerName: `${t('Country')}`,
      cellClass: 'hide-selection-checkbox',
      editable: false,
      filter: "agSetColumnFilter",
      valueGetter: ({ data }) => {
        if (data.country && data.country.toLowerCase().includes('000')) {
          return ''; // Retorna una cadena vacía si el campo contiene 'empty'
        } else {
          return data.country; // De lo contrario, devuelve el valor original del campo
        }
      }
    },
    {
      field: 'isActive',
      headerName: `${t('Status')}`,
      cellClass: 'hide-selection-checkbox',
      editable: false,
      cellRenderer: 'statusRenderer',
      filter: "agSetColumnFilter",
      valueGetter: ({ data }) => data.isActive
    },
    {
      cellRenderer: "editRenderer",
      valueSetter: ({ onEdit }) => onEdit,
      cellRendererParams: {
        onEdit
      },
      editable: false,
      cellStyle: {
        display: "flex",
        //"justify-content": "center",
        //"align-items": "center",
        border: "none !important"
      },
      cellClass: 'hide-selection-checkbox',
      suppressNavigable: true,
      suppressCellFlash: true,
      resizable: true,
      filter: false,
      floatingFilter: false
    }

  ];
};

export const getUsersFetchByEmail = () => ({
  fetchBy: {
    allUsers: {
      type: 'allUsers',
      dataKey: 'allUsers',
      mapStrategy: (user) => {
        const { id, email, firstName, lastName } = user;
        return {
          text: `${email} - ${firstName} ${lastName}`,
          value: id,
          payload: user,
        };
      },
      customFetchFunction: getAllUsers,
    },
  },
});

export const getFetchOrganizationUsers = async (organization, token) => ({
  fetchBy: {
    usersOrg: {
      type: 'users',
      dataKey: 'users',
      mapStrategy: (user) => {
        const { id, firstName, lastName } = user;
        return {
          text: `${firstName} ${lastName}`,
          value: id,
          payload: user,
        };
      },
      customFetchFunction: await getOrganizationUsers(organization, token),
    },
  },
});
export const getTenantsFetchBySpecification = () => ({
  fetchBy: {
    allTenants: {
      type: 'allTenants',
      dataKey: 'allTenants',
      mapStrategy: (tenant) => {
        const { id, name } = tenant;
        return {
          text: `${name}`,
          value: id,
          payload: tenant,
        };
      },
      customFetchFunction: getAllTenants,
    },
  },
});

export const getNotificationsFetchBySpecification = () => ({
  fetchBy: {
    notification: {
      type: 'notification',
      dataKey: 'notification',
      mapStrategy: (notification) => {
        const { id, title, isActive } = notification;
        return {
          text: `${title} - ${isActive ? 'ACTIVE' : 'INACTIVE'}`,
          value: id,
          payload: notification,
        };
      },
      customFetchFunction: getPortalNotification,
    },
  },
});

export const getCarriersFetchBySpecification = () => ({
  fetchBy: {
    carriers: {
      type: "carriers",
      dataKey: "carriers",
      mapStrategy: (carrier) => {
        const { carrierId, scac, carrierName } = carrier;
        return {
          text: `(${scac}) ${carrierName}`,
          value: carrierId,
          payload: carrier
        };
      },
      customFetchFunction: getAllCarriers
    }
  }
});

export const getCarriersFilteredFetchBySpecification = () => ({
  fetchBy: {
    carriers: {
      type: "carriers",
      dataKey: "carriers",
      mapStrategy: (carrier) => {
        const { carrierId, scac, carrierName } = carrier;
        return {
          text: `(${scac}) ${carrierName}`,
          value: carrierId,
          payload: carrier
        };
      },
      customFetchFunction: getAllCarriers,
      isActive: true
    }
  }
});